import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { RentripsURL } from "utils/request";
import { ObjectType } from "../types";
import { DataType } from "../types/dataType";
import { ResponseType } from "../types/dataType";
import { LogResponse, ILog } from "../types/logType";
import config from "../utils/cookieConfig";

const turnQuery = (opts: {}) =>
	Object.entries(opts)
		.map(([key, value]) => `${key}=${value}`)
		.join("&");

export const useFetch = <T extends {}>(
	opts: T,
	type?: "group" | "count"
): [DataType, boolean] => {
	const [result, setResult] = useState<DataType>({
		data: [[]],
		head: [],
		total: {},
	});
	const [loading, setLoading] = useState(false);

	const fetchData = useCallback(async () => {
		try {
			setLoading(true);
			const { data }: { data: ResponseType<DataType> } = await axios.get(
				`${RentripsURL}/api/analytic${type ? "/log/" + type : ""}?${turnQuery(
					opts
				)}`,
				config
			);

			console.log(data);

			if (data.success) {
				setResult(data.data);
				setLoading(false);
			}
		} catch (error: unknown) {
			if (error instanceof Error) {
				if (error.message.includes("403")) {
					return (window.location.href = "/login");
				}
			}
			console.error(error);
		}
	}, [opts, type]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	return [result, loading];
};

export const useLogs = (
	opts: Partial<ILog>,
	limit?: number
): [Array<ILog>, boolean] => {
	const [logs, setLogs] = useState<Array<ILog>>([]);
	const [loading, setLoading] = useState(false);

	const fetchLogs = async (opt: Partial<ILog>, lim?: number) => {
		setLoading(true);
		try {
			const { data }: { data: LogResponse } = await axios.get(
				RentripsURL + `/api/analytic/log?${turnQuery(opt)}&limit=${lim}`,
				config
			);
			if (data.success)
				setTimeout(() => {
					setLoading(false);
					setLogs(data.data);
				}, 300);
		} catch (error) {
			setLoading(false);
			console.error("Failed when fetch data");
		}
	};

	useEffect(() => {
		fetchLogs(opts, limit);
	}, [opts, limit]);

	return [logs, loading];
};

export const useSingleItem = <T>(
	id: string,
	type?: ObjectType
): [Partial<T>, boolean] => {
	const [item, setItem] = useState<Partial<T>>({});
	const [loading, setLoading] = useState(false);

	const fItem = useCallback(async () => {
		setLoading(true);
		try {
			const { data }: { data: ResponseType<T> } = await axios.get(
				RentripsURL + `/api/analytic/${type || "product"}/${id}`,
				config
			);
			if (data.success)
				setTimeout(() => {
					setLoading(false);
					setItem(data.data);
				}, 300);
		} catch (error) {
			setLoading(false);
			console.error("Failed when fetch data");
		}
	}, [id, type]);

	useEffect(() => {
		fItem();
	}, [fItem]);

	return [item, loading];
};

export type ItemProps = {
	type: ObjectType;
	all: boolean;
	page: number;
	ownerId: string;
};

export const useItems = <T>(
	logs: Array<ILog>,
	opts?: Partial<ItemProps>
): [Array<T>, number, boolean] => {
	const [item, setItem] = useState<Array<T>>([]);
	const [length, setLength] = useState<number>(1);
	const [loading, setLoading] = useState(false);

	const fetchItems = useCallback(async () => {
		setLoading(true);
		if (!logs.length && !opts?.all) {
			setLoading(false);
			return setItem([]);
		}

		const q = turnQuery(opts || {});

		try {
			const modifiedArr = logs
				.map((log) => (opts?.type === "user" ? log?.user : log.target))
				.filter((id) => !id.startsWith("::"))
				.join(",");

			const { data }: { data: ResponseType<Array<T>> } = await axios.get(
				RentripsURL +
					`/api/analytic/${opts?.type || "product"}?${q}${
						modifiedArr ? "&ids=" + modifiedArr : ""
					}`,
				config
			);

			if (data.success)
				setTimeout(() => {
					setLoading(false);
					setItem(data.data);
					setLength(data.length);
				}, 300);
		} catch (error) {
			setLoading(false);
			setItem([]);
			console.error(error);
		}
	}, [logs, opts]);

	useEffect(() => {
		fetchItems();
	}, [fetchItems]);

	return [item, length, loading];
};
